/** QR scanner using the native Camera API (getUserMedia).
 *  Renders a video preview and scans for QR codes via BarcodeDetector. */

export interface QrResult {
  url: string;
  token: string;
}

/** Start the QR camera scanner inside the given container. */
export async function startQrScanner(
  container: HTMLElement,
  onResult: (result: QrResult) => void,
): Promise<void> {
  const video = document.createElement('video');
  video.className = 'qr-video';
  video.setAttribute('autoplay', '');
  video.setAttribute('playsinline', '');
  container.innerHTML = '';
  container.appendChild(video);

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
    });
    video.srcObject = stream;

    // Use BarcodeDetector if available (Chrome 83+, Edge, Android WebView)
    if ('BarcodeDetector' in window) {
      const detector = new (window as unknown as { BarcodeDetector: BarcodeDetectorConstructor }).BarcodeDetector({
        formats: ['qr_code'],
      });

      const scanLoop = async (): Promise<void> => {
        if (video.readyState < 2) {
          requestAnimationFrame(() => { void scanLoop(); });
          return;
        }
        try {
          const barcodes = await detector.detect(video);
          if (barcodes.length > 0) {
            const raw = barcodes[0]?.rawValue ?? '';
            stream.getTracks().forEach((t) => t.stop());
            onResult(parseQrData(raw));
            return;
          }
        } catch { /* ignore detection errors */ }
        requestAnimationFrame(() => { void scanLoop(); });
      };
      void scanLoop();
    } else {
      container.innerHTML += '<p class="qr-fallback">QR scanning not supported in this browser. Please enter the URL manually.</p>';
    }
  } catch {
    container.innerHTML = '<p class="error-text">Camera access denied</p>';
  }
}

/** Parse QR data — expected format: "hub://url?token=xyz" or plain URL. */
function parseQrData(raw: string): QrResult {
  const cleaned = raw.replace(/^hub:\/\//, 'http://');
  try {
    const u = new URL(cleaned);
    return {
      url: `${u.protocol}//${u.host}`,
      token: u.searchParams.get('token') ?? '',
    };
  } catch {
    return { url: raw, token: '' };
  }
}

/** TypeScript types for BarcodeDetector (not in lib.dom yet). */
interface BarcodeDetectorConstructor {
  new (opts: { formats: string[] }): BarcodeDetectorInstance;
}
interface BarcodeDetectorInstance {
  detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
}
